import React, { useEffect, useMemo, useState } from 'react'
import datasetService from '../services/datasetService'
import trainService, { TrainRunDetail, TrainRunSummary } from '../services/trainService'

interface Props {
  isOpen: boolean
  onClose: () => void
  taskId: number
  taskLabel?: string
  onCreated?: (run: TrainRunDetail) => void | Promise<void>
}

type DsNode = { id: number; name: string; kind: 'project' | 'dataset'; parent_id?: number | null; description?: string | null }

export default function CreateTrainRunModal({ isOpen, onClose, taskId, taskLabel, onCreated }: Props) {
  const [nodes, setNodes] = useState<DsNode[]>([])
  const [projectMap, setProjectMap] = useState<Record<number, string>>({})
  const [datasetNodeId, setDatasetNodeId] = useState<number | undefined>(undefined)
  const [datasetJobId, setDatasetJobId] = useState<number | undefined>(undefined)
  const [datasetReady, setDatasetReady] = useState<boolean>(false)
  const [datasetStatusText, setDatasetStatusText] = useState<string>('')

  const [codeRepoUrl, setCodeRepoUrl] = useState('')
  const [codeCommitHash, setCodeCommitHash] = useState('')
  const [parametersText, setParametersText] = useState('{}')
  const [metricsText, setMetricsText] = useState('{}')
  const [trainingEnvText, setTrainingEnvText] = useState('{}')
  const [notes, setNotes] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  // Task 필수항목
  type ReqField = { key: string; input_type: 'int'|'float'|'string'|'text' }
  const [taskReqFields, setTaskReqFields] = useState<ReqField[]>([])
  const [taskReqValues, setTaskReqValues] = useState<Record<string, string>>({})

  // 기타 입력(사용자 정의)
  type ExtraField = ReqField
  const [extraFields, setExtraFields] = useState<ExtraField[]>([])
  const [extraValues, setExtraValues] = useState<Record<string, string>>({})
  const [newExtraKey, setNewExtraKey] = useState<string>('')
  const [newExtraType, setNewExtraType] = useState<ExtraField['input_type']>('string')
  const [extraError, setExtraError] = useState<string | null>(null)

  // 데이터셋 요약(자동 병합용)
  const [dsSummary, setDsSummary] = useState<any | null>(null)
  // 실험 시간
  const [startedAt, setStartedAt] = useState('')
  const [finishedAt, setFinishedAt] = useState('')

  // 기타 입력 필드 추가/삭제
  const addExtraField = () => {
    const raw = (newExtraKey || '').trim()
    if (!raw) return
    // 중복: 기타 입력 + 필수항목 모두에서 검사
    const dupInExtras = extraFields.some(f => (f.key || '').trim().toLowerCase() === raw.toLowerCase())
    const dupInRequired = taskReqFields.some(f => (f.key || '').trim().toLowerCase() === raw.toLowerCase())
    if (dupInExtras || dupInRequired) { setExtraError(`'${raw}' 항목은 이미 존재합니다.`); return }
    setExtraFields(prev => [...prev, { key: raw, input_type: newExtraType }])
    setExtraValues(prev => ({ ...prev, [raw]: '' }))
    if (extraError) setExtraError(null)
    setNewExtraKey('')
    setNewExtraType('string')
  }
  const removeExtraField = (idx: number) => {
    setExtraFields(prev => prev.filter((_, i) => i !== idx))
  }

  // body scroll lock (prevent background scroll when modal open)
  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [isOpen])

  // 저장/아티팩트 업로드 중에는 전역 커서를 로딩 상태로 표시
  useEffect(() => {
    const prev = document.body.style.cursor
    if (submitting) {
      document.body.style.cursor = 'wait'
    }
    return () => {
      document.body.style.cursor = prev
    }
  }, [submitting])

  useEffect(() => {
    if (!isOpen) return
    ;(async () => {
      try {
        const list = await datasetService.listProjects()
        const pmap: Record<number, string> = {}
        list.filter((n: any) => n.kind === 'project').forEach((p: any) => { pmap[p.id] = p.name })
        setNodes(list as any)
        setProjectMap(pmap)
      } catch {
        setNodes([])
        setProjectMap({})
      }
      setDatasetNodeId(undefined)
      setDatasetJobId(undefined)
      setDatasetReady(false)
      setDatasetStatusText('')
      setCodeRepoUrl('')
      setCodeCommitHash('')
      setParametersText('{}')
      setMetricsText('{}')
      setTrainingEnvText('{}')
      setNotes('')
      setError(null)
      // 간편 입력/환경 리셋
      setDsSummary(null)
      setStartedAt(''); setFinishedAt('')
      setExtraFields([]); setExtraValues({}); setNewExtraKey(''); setNewExtraType('string'); setExtraError(null)
      // Task 필수항목 로딩
      try {
        const t = await trainService.getTask(taskId)
        const fields = (t?.required_fields as any) || []
        setTaskReqFields(fields as ReqField[])
        const initial: Record<string, string> = {}
        for (const f of fields as ReqField[]) initial[f.key] = ''
        setTaskReqValues(initial)
      } catch {
        setTaskReqFields([])
        setTaskReqValues({})
      }
    })()
  }, [isOpen, taskId])

  const datasetOptions = useMemo(() => {
    const projects = new Map<number, string>()
    nodes.filter(n => n.kind === 'project' && (n.parent_id == null)).forEach(p => projects.set(p.id, p.name))
    return nodes
      .filter(n => n.kind === 'dataset')
      .map(ds => ({ id: ds.id, label: `${projects.get(ds.parent_id as number) ?? '프로젝트'} > ${ds.name}` }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
  }, [nodes])

  const parseJsonSafe = (text: string) => { try { return text ? JSON.parse(text) : {} } catch { return {} } }

  const fetchLatestForDataset = async (dsId: number) => {
    setDatasetReady(false)
    setDatasetJobId(undefined)
    setDatasetStatusText('확인 중...')
    try {
      const latest = await datasetService.getLatestSummary(dsId)
      if (!latest?.ready) {
        setDatasetStatusText('최신 데이터셋이 준비되지 않았습니다')
        setDatasetReady(false)
        return
      }
      // 상세 요약(여기서 job_id 제공)
      const summary = await datasetService.getSummary(dsId)
      if (summary && summary.job_id) {
        setDatasetJobId(Number(summary.job_id))
        setDatasetReady(true)
        setDatasetStatusText(`준비됨 · job #${summary.job_id} · 총 ${summary.total.toLocaleString()}개`)
        setDsSummary(summary)
      } else {
        setDatasetStatusText('요약 정보를 가져오지 못했습니다')
        setDatasetReady(false)
        setDsSummary(null)
      }
    } catch {
      setDatasetReady(false)
      setDatasetStatusText('조회 실패')
      setDsSummary(null)
    }
  }

  // 아티팩트 파일 선택/타입 입력
  type LocalFile = { file: File; type: string }
  const [artifacts, setArtifacts] = useState<LocalFile[]>([])
  const allowed = new Set(['.pt', '.onnx', '.hef', '.yaml', '.yml', '.md', '.txt', '.log', '.png', '.jpg', '.jpeg', '.svg', '.pdf'])
  const guessType = (name: string) => {
    const lower = name.toLowerCase()
    if (lower.includes('confusion')) return 'confusion_matrix'
    if (/(roc|pr|curve|loss|acc|accuracy)/.test(lower)) return 'plot'
    if (lower.endsWith('.pt')) return 'pt'
    if (lower.endsWith('.onnx')) return 'onnx'
    if (lower.endsWith('.hef')) return 'hef'
    if (lower.endsWith('.yaml') || lower.endsWith('.yml')) return 'yaml'
    if (lower.endsWith('.md')) return 'md'
    if (lower.endsWith('.txt')) return 'txt'
    if (lower.endsWith('.log')) return 'log'
    if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.svg')) return 'image'
    if (lower.endsWith('.pdf')) return 'pdf'
    return ''
  }
  const onPickFiles = (list: FileList | null) => {
    if (!list) return
    const arr: LocalFile[] = []
    const invalid: string[] = []
    for (let i = 0; i < list.length; i++) {
      const f = list.item(i) as File
      const ext = `.${f.name.toLowerCase().split('.').pop()}`
      if (!allowed.has(ext)) {
        invalid.push(f.name)
        continue
      }
      arr.push({ file: f, type: guessType(f.name) })
    }
    setArtifacts(prev => [...prev, ...arr])
    if (invalid.length > 0) {
      setError(`허용되지 않는 확장자 파일이 제외되었습니다: ${invalid.join(', ')}`)
    }
  }
  const onRemove = (idx: number) => setArtifacts(prev => prev.filter((_, i) => i !== idx))

  

  const onUploadJson = async (file: File) => {
    try {
      const text = await file.text()
      const obj = JSON.parse(text)
      // 데이터셋 선택은 사용자가 고른 값을 유지. 비어있을 때만 JSON의 값을 반영
      if (!datasetNodeId && obj.dataset_node_id) {
        const dsId = Number(obj.dataset_node_id)
        setDatasetNodeId(dsId)
        await fetchLatestForDataset(dsId)
      }
      if (obj.code_repo_url) setCodeRepoUrl(String(obj.code_repo_url))
      if (obj.code_commit_hash) setCodeCommitHash(String(obj.code_commit_hash))
      if (obj.parameters) setParametersText(JSON.stringify(obj.parameters, null, 2))
      if (obj.metrics) setMetricsText(JSON.stringify(obj.metrics, null, 2))
      if (obj.training_env_info) setTrainingEnvText(JSON.stringify(obj.training_env_info, null, 2))
      if (obj.notes) setNotes(String(obj.notes))
      setAdvancedOpen(true)
    } catch (e: any) {
      setError('JSON 파싱 실패: ' + (e?.message || ''))
    }
  }

  const handleSubmit = async () => {
    if (!taskId) { setError('Task가 선택되지 않았습니다'); return }
    if (!datasetNodeId || !datasetReady || !datasetJobId) { setError('준비된 데이터셋을 선택하세요'); return }
    // 필수항목 검증
    for (const f of taskReqFields) {
      const v = (taskReqValues[f.key] ?? '').trim()
      if (v === '') { setError(`필수항목 '${f.key}'을(를) 입력하세요`); return }
      if (f.input_type === 'int' && !/^[-+]?\d+$/.test(v)) { setError(`'${f.key}'에는 정수를 입력하세요`); return }
      if (f.input_type === 'float' && !/^[-+]?\d*(?:\.\d+)?$/.test(v)) { setError(`'${f.key}'에는 실수를 입력하세요`); return }
    }
    // 기타입력 타입 검증(비어있는 값은 허용)
    for (const f of extraFields) {
      const v = (extraValues[f.key] ?? '').trim()
      if (!v) continue
      if (f.input_type === 'int' && !/^[-+]?\d+$/.test(v)) { setError(`기타입력 '${f.key}'에는 정수를 입력하세요`); return }
      if (f.input_type === 'float' && !/^[-+]?\d*(?:\.\d+)?$/.test(v)) { setError(`기타입력 '${f.key}'에는 실수를 입력하세요`); return }
    }
    setError(null)
    try {
      setSubmitting(true)
      // parameters 초기화
      const params: any = {}
      const mets: any = {}
      // 데이터셋 메타 입력은 삭제 (선택 시 자동 병합)
      // 데이터셋 요약 자동 병합 (선택된 경우)
      if (dsSummary) {
        params.dataset = {
          ...(params.dataset || {}),
          node_id: datasetNodeId,
          job_id: datasetJobId,
          label_set_id: dsSummary.label_set_id,
          label_format: dsSummary.label_format,
          label_version_name: dsSummary.label_version_name,
          counts: { total: dsSummary.total, train: dsSummary.train, val: dsSummary.val, test: dsSummary.test },
          sampling: dsSummary.sampling,
          output: dsSummary.output,
        }
      }
      // 학습 환경 제거 → 빈 객체 유지
      const trainingEnv: any = {}

      // 필수항목 값 병합 (parameters.required)
      const requiredMap: Record<string, any> = {}
      for (const f of taskReqFields) {
        const raw = taskReqValues[f.key]
        let val: any = raw
        if (f.input_type === 'int') val = parseInt(raw, 10)
        if (f.input_type === 'float') val = parseFloat(raw)
        requiredMap[f.key] = val
      }
      if (Object.keys(requiredMap).length > 0) params.required = requiredMap

      // 기타 입력 병합 (parameters.extras)
      const extrasMap: Record<string, any> = {}
      for (const f of extraFields) {
        const raw = extraValues[f.key]
        if (typeof raw === 'undefined') continue
        let val: any = raw
        if (f.input_type === 'int' && raw.trim() !== '') val = parseInt(raw, 10)
        if (f.input_type === 'float' && raw.trim() !== '') val = parseFloat(raw)
        extrasMap[f.key] = val
      }
      if (Object.keys(extrasMap).length > 0) params.extras = extrasMap

      const payload = {
        task_id: taskId,
        dataset_node_id: datasetNodeId,
        dataset_job_id: datasetJobId,
        // 코드정보 입력 제거
        parameters: params,
        metrics: mets,
        training_env_info: trainingEnv,
        notes: notes || undefined,
        status: 'completed',
        started_at: startedAt ? new Date(startedAt).toISOString() : undefined,
        finished_at: finishedAt ? new Date(finishedAt).toISOString() : undefined,
      }
      const run = await trainService.createRun(payload)
      // 아티팩트가 있다면 즉시 함께 업로드
      if (artifacts.length > 0) {
        await trainService.uploadArtifacts(run.id, artifacts.map(a => a.file), artifacts.map(a => a.type))
      }
      setSubmitting(false)
      if (onCreated) await onCreated(run)
      onClose()
    } catch (e: any) {
      setSubmitting(false)
      setError(e?.message || '등록 실패')
    }
  }

  if (!isOpen) return null

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${submitting ? 'cursor-wait' : ''}`}>
      <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">학습 이력 등록</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>

        <div className="px-6 pb-4 flex-1 overflow-y-auto overscroll-contain">
          <div className="space-y-4">
          <div>
            <div className="text-xs text-gray-600 mb-1">선택된 작업 카테고리</div>
            <div className="px-2 py-1 bg-gray-100 rounded text-sm text-gray-800 inline-flex items-center gap-2">
              {taskLabel && taskLabel.trim().length > 0 ? taskLabel : `#${taskId}`}
            </div>
          </div>
          {/* 데이터셋 선택 - 최상단 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">데이터셋 선택 <span className="text-red-500">*</span></label>
            <select
              className="w-full border rounded px-2 py-2 text-sm"
              value={datasetNodeId || ''}
              onChange={async (e) => {
                const val = e.target.value ? Number(e.target.value) : undefined
                setDatasetNodeId(val)
                if (val) await fetchLatestForDataset(val)
              }}
            >
              <option value="">선택하세요</option>
              {datasetOptions.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
            {datasetNodeId && (
              <div className="mt-2 text-xs text-gray-700">
                {datasetReady && dsSummary ? (
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-100 text-green-800">준비됨</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-800">job #{dsSummary.job_id ?? datasetJobId}</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-800">총 {dsSummary.total?.toLocaleString?.() || dsSummary.total}개</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-100 text-green-800">Train {dsSummary.train} ({dsSummary.total ? Math.round(dsSummary.train / dsSummary.total * 100) : 0}%)</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-800">Val {dsSummary.val} ({dsSummary.total ? Math.round(dsSummary.val / dsSummary.total * 100) : 0}%)</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-yellow-100 text-yellow-800">Test {dsSummary.test} ({dsSummary.total ? Math.round(dsSummary.test / dsSummary.total * 100) : 0}%)</span>
                  </div>
                ) : (
                  <div className="text-gray-700">{datasetStatusText}</div>
                )}
              </div>
            )}
          </div>

          {/* 아티팩트 업로드 - 최상단 */}
          <div className="border rounded">
            <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-800">아티팩트 파일</div>
                <div className="text-xs text-gray-500">가중치/ONNX/HEF/Config/README/Log/시각화(PNG/JPG/SVG/PDF)<br/>파일을 함께 업로드할 수 있습니다.</div>
              </div>
              <label className="px-3 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 cursor-pointer whitespace-nowrap shrink-0">
                파일 선택
                <input type="file" multiple className="hidden" accept=".pt,.onnx,.hef,.yaml,.yml,.md,.txt,.log,.png,.jpg,.jpeg,.svg,.pdf" onChange={(e) => onPickFiles(e.target.files)} />
              </label>
            </div>
            {artifacts.length > 0 ? (
              <div className="max-h-48 overflow-auto divide-y">
                {artifacts.map((a, idx) => (
                  <div key={`${a.file.name}-${idx}`} className="flex items-center gap-2 px-3 py-2">
                    <div className="flex-1 truncate text-sm text-gray-800" title={a.file.name}>{a.file.name}</div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs" title="파일 확장자로 자동 인식된 타입">
                      {a.type || '-'}
                    </span>
                    <button className="text-xs text-red-600 hover:underline" onClick={() => onRemove(idx)}>제거</button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-3 py-6">
                <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center text-sm text-gray-500">
                  상단의 “파일 선택” 버튼을 눌러 아티팩트를 추가하세요.
                </div>
              </div>
            )}
          </div>
          

          {/* 학습 환경 파트 제거 */}
          {/* 핵심 입력 */}
          <div className="border rounded">
            <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium text-gray-800">핵심 입력</div>
            <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Task 필수항목 */}
              {taskReqFields.length > 0 && (
                <div className="md:col-span-2">
                  <div className="text-xs text-gray-500 mb-2">필수항목</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {taskReqFields.map((f) => (
                      <div key={f.key}>
                        <label className="block text-xs text-gray-600 mb-1">{f.key} ({f.input_type}) <span className="text-red-500">*</span></label>
                        {f.input_type === 'text' ? (
                          <textarea className="w-full border rounded px-2 py-1 text-sm resize-none" rows={3} value={taskReqValues[f.key] || ''} onChange={e => setTaskReqValues(v => ({ ...v, [f.key]: e.target.value }))} />
                        ) : (
                          <input className="w-full border rounded px-2 py-1 text-sm" value={taskReqValues[f.key] || ''} onChange={e => setTaskReqValues(v => ({ ...v, [f.key]: e.target.value }))} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* 기타 입력은 별도 섹션에서 처리 */}

              

              {/* 학습기간은 별도 섹션으로 이동 */}
            </div>
          </div>
          {/* 기타 입력 */}
          <div className="border rounded">
            <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium text-gray-800 flex items-center justify-between">
              <span>기타 입력 (선택)</span>
              {extraError && <span className="text-xs text-red-600">{extraError}</span>}
            </div>
            <div className="p-3 space-y-3">
              <div className="grid grid-cols-4 gap-2">
                <input className="col-span-2 border rounded px-2 py-1 text-sm" placeholder="키 (예: note, owner)" value={newExtraKey} onChange={e => setNewExtraKey(e.target.value)} />
                <div className="flex gap-2 col-span-2">
                  <select className="border rounded px-2 py-1 text-sm flex-1" value={newExtraType} onChange={e => setNewExtraType(e.target.value as any)}>
                    <option value="int">int</option>
                    <option value="float">float</option>
                    <option value="string">string</option>
                    <option value="text">text</option>
                  </select>
                  <button className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 shrink-0 whitespace-nowrap" onClick={addExtraField}>추가</button>
                </div>
              </div>
              {extraFields.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {extraFields.map((f, idx) => (
                    <div key={`${f.key}-${idx}`}>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs text-gray-600">{f.key} ({f.input_type})</label>
                        <button className="text-xs text-red-600 hover:underline" onClick={() => removeExtraField(idx)}>제거</button>
                      </div>
                      {f.input_type === 'text' ? (
                        <textarea className="w-full border rounded px-2 py-1 text-sm resize-none" rows={3} value={extraValues[f.key] || ''} onChange={e => setExtraValues(v => ({ ...v, [f.key]: e.target.value }))} />
                      ) : (
                        <input className="w-full border rounded px-2 py-1 text-sm" value={extraValues[f.key] || ''} onChange={e => setExtraValues(v => ({ ...v, [f.key]: e.target.value }))} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* 학습기간 (별도 섹션) */}
          <div className="border rounded">
            <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium text-gray-800">학습기간</div>
            <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">시작</label>
                <input type="datetime-local" className="w-full border rounded px-2 py-1 text-sm" value={startedAt} onChange={e => setStartedAt(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">종료</label>
                <input type="datetime-local" className="w-full border rounded px-2 py-1 text-sm" value={finishedAt} onChange={e => setFinishedAt(e.target.value)} />
              </div>
            </div>
          </div>
          {/* 메모 (핵심 입력 아래, 고급 입력 위) */}
          <div className="border rounded">
            <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium text-gray-800">메모 (선택)</div>
            <div className="p-3">
              <textarea className="w-full border rounded px-2 py-1 text-sm resize-none" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="실험 비고/메모를 입력하세요" />
            </div>
          </div>

          {/* 고급 입력 섹션 제거 */}

          {error && <div className="text-sm text-red-600">{error}</div>}
          </div>
        </div>

        

        <div className="px-6 py-4 flex justify-end gap-2">
          <button className="px-3 py-2 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50" onClick={onClose} disabled={submitting}>취소</button>
          <button
            className="px-3 py-2 text-sm rounded text-white bg-gray-800 hover:bg-gray-900 disabled:opacity-50"
            disabled={submitting}
            onClick={handleSubmit}
          >{submitting ? '저장 중...' : '저장'}</button>
        </div>
      </div>
    </div>
  )
}


